import { Badge, Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAllowedActions,
  setPage,
  setPageSize,
} from '../../app/approvalQueue/approvalQueueSlice';
import {
  GetApiApprovalQueueApiArg,
  useGetApiApprovalQueueQuery,
} from '../../app/athenaApiGen';
import { RootState } from '../../app/store';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../utils/tableVariables';
import ApprovalQueueTable from './components/ApprovalQueueTable';
import Header from './components/Header';
import ViewPanel from './components/ViewPanel';
import { QueueContainer, QueueContentContainer } from './StyledComponents';

/**
 *
 * @component
 * @returns {JSX.Element} The rendered Approval Queue component.
 */
const ApprovalQueue = ({
  hideHeader = false,
  fixedPageSize,
}: {
  hideHeader?: boolean;
  fixedPageSize?: number;
}) => {
  const dispatch = useDispatch();
  const { actions } = useSelector((state: RootState) => state.user);
  const { viewPanel, expandedPanel } = useSelector(
    (state: RootState) => state.approvalQueue,
  );
  const tabs = ['Open', 'Approved', 'Rejected', 'View All'];
  const [isBadgeLoading, setIsBadgeLoading] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<number[]>([]);
  const [tabValue, setTabValue] = useState(0);

  const tabArgs: GetApiApprovalQueueApiArg[] = useMemo(
    () => [
      { page: 1, limit: 1, approvalState: 'pending' }, //Pending
      { page: 1, limit: 1, approvalState: 'approved' }, // Approved
      {
        page: 1,
        limit: 1,
        approvalState: 'rejected',
      }, // Rejected
      {
        page: 1,
        limit: 1,
        approvalState: 'all',
      }, // View All
    ],
    [],
  );

  const qPending = useGetApiApprovalQueueQuery(tabArgs[0]);
  const qSubmitted = useGetApiApprovalQueueQuery(tabArgs[1]);
  const qCompleted = useGetApiApprovalQueueQuery(tabArgs[2]);
  const qAll = useGetApiApprovalQueueQuery(tabArgs[3]);

  const counts = useMemo(
    () => [
      qPending?.data?.totalCount ?? 0,
      qSubmitted?.data?.totalCount ?? 0,
      qCompleted?.data?.totalCount ?? 0,
      qAll?.data?.totalCount ?? 0,
    ],
    [
      qPending?.data?.totalCount,
      qSubmitted?.data?.totalCount,
      qCompleted?.data?.totalCount,
      qAll?.data?.totalCount,
    ],
  );

  const anyLoading = Boolean(
    qAll?.isLoading ||
      qAll?.isFetching ||
      qPending?.isLoading ||
      qPending?.isFetching ||
      qSubmitted?.isLoading ||
      qSubmitted?.isFetching ||
      qCompleted?.isLoading ||
      qCompleted?.isFetching,
  );

  useEffect(() => {
    setIsBadgeLoading(anyLoading);
    setBadgeCounts(counts);
  }, [anyLoading, counts]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    dispatch(setPage(0));
    dispatch(setPageSize(DEFAULT_TABLE_PAGE_SIZE));
    setTabValue(newValue);
  };

  useEffect(() => {
    const userAllowedActions = actions?.filter((action) =>
      action?.type?.includes('Approve'),
    );

    dispatch(setAllowedActions(userAllowedActions));
  }, [actions, dispatch]);

  return (
    <QueueContainer sx={{ p: hideHeader ? 0 : 3 }}>
      {!hideHeader && <Header />}
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          borderColor: 'divider',
          paddingTop: 1,
          ...(hideHeader && {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff',
            ...theme.applyStyles('dark', {
              backgroundColor: '#000',
            }),
          }),
        })}
      >
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="deliverables tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-indicator': {
              backgroundColor: '#1f4D83',
            },
            '& .MuiTab-root': {
              color: 'text.primary',
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: 'none',
              },
              '& .Mui-selected': {
                outline: 'none',
                color: '#1f4D83',
                '& span': {
                  color: '#1f4D83',
                },
              },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              sx={{
                textTransform: 'none',
                minHeight: 'auto',
                py: 1.5,
              }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    justifyContent: 'center',
                  }}
                >
                  <span>{tab}</span>
                  {isBadgeLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Badge
                      badgeContent={badgeCounts[index] ?? 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          // Only paint the Open badge orange when there's
                          // something to act on. Empty Open reads as
                          // neutral, matching the other tabs.
                          backgroundColor:
                            tab === 'Open' && (badgeCounts[index] ?? 0) > 0
                              ? '#F57820'
                              : '#1F4D83',
                          color: '#FFFFFF',
                        },
                      }}
                      showZero
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
      <QueueContentContainer>
        {!expandedPanel && (
          <ApprovalQueueTable
            approvalState={tabArgs[tabValue].approvalState}
            fixedPageSize={hideHeader ? (fixedPageSize ?? 5) : undefined}
          />
        )}
        {viewPanel && <ViewPanel />}
      </QueueContentContainer>
    </QueueContainer>
  );
};

export default ApprovalQueue;
